/**
 * Assets: district property — one record per physical thing, its identity
 * (asset number, tag, barcode, QR token, every identifier it ever had), who
 * owns it, where it is, who has it, and a history that is never rewritten.
 *
 * Three things are kept apart on purpose:
 *   - owner     — the school or department the asset belongs to;
 *   - location  — where it physically is now;
 *   - custodian — who is responsible for it now (a student, staff member,
 *                 room, department, team, vehicle or storage).
 *
 * Assignments, temporary checkouts and loaners are effective-dated records:
 * a change ends one and starts another, so "who had AST-10025 in March" has
 * an answer. Purchasing owns acquisition, Facilities owns repair work,
 * Fees owns money; Assets references each of them.
 */

import type { BaseDocument } from '../platform/documents.js'

// ─────────────────────────────────────────────────────────────────────────────
// Vocabulary
// ─────────────────────────────────────────────────────────────────────────────

/** Default types. The district relabels, adds and configures them in Settings; records store the key. */
export const ASSET_TYPES = [
  'chromebook',
  'laptop',
  'desktop',
  'tablet',
  'monitor',
  'printer',
  'projector',
  'camera',
  'radio',
  'phone',
  'hotspot',
  'calculator',
  'textbook',
  'furniture',
  'instrument',
  'athletic_equipment',
  'uniform',
  'kitchen_equipment',
  'maintenance_equipment',
  'vehicle_equipment',
  'av_equipment',
  'aed',
  'equipment',
  'other',
] as const
export type AssetType = (typeof ASSET_TYPES)[number]
export const ASSET_TYPE_LABELS: Record<AssetType, string> = {
  chromebook: 'Chromebook',
  laptop: 'Laptop',
  desktop: 'Desktop',
  tablet: 'Tablet',
  monitor: 'Monitor',
  printer: 'Printer',
  projector: 'Projector',
  camera: 'Camera',
  radio: 'Radio',
  phone: 'Phone',
  hotspot: 'Hotspot',
  calculator: 'Calculator',
  textbook: 'Textbook',
  furniture: 'Furniture',
  instrument: 'Musical instrument',
  athletic_equipment: 'Athletic equipment',
  uniform: 'Uniform',
  kitchen_equipment: 'Kitchen equipment',
  maintenance_equipment: 'Maintenance equipment',
  vehicle_equipment: 'Vehicle-linked equipment',
  av_equipment: 'AV equipment',
  aed: 'AED',
  equipment: 'Equipment',
  other: 'Other',
}

/** Conditions in order, best first. `broken` is the original word for nonfunctional. */
export const ASSET_CONDITIONS = ['new', 'excellent', 'good', 'fair', 'poor', 'damaged', 'nonfunctional', 'broken'] as const
export type AssetCondition = (typeof ASSET_CONDITIONS)[number]
export const ASSET_CONDITION_LABELS: Record<AssetCondition, string> = { new: 'New', excellent: 'Excellent', good: 'Good', fair: 'Fair', poor: 'Poor', damaged: 'Damaged', nonfunctional: 'Nonfunctional', broken: 'Nonfunctional' }
export const CONDITION_RANK: Record<AssetCondition, number> = { new: 0, excellent: 1, good: 2, fair: 3, poor: 4, damaged: 5, nonfunctional: 6, broken: 6 }
export const normalizeAssetCondition = (c: string | null | undefined): AssetCondition => (c === 'broken' ? 'nonfunctional' : c && (ASSET_CONDITIONS as readonly string[]).includes(c) ? (c as AssetCondition) : 'good')

export const ASSET_STATUSES = [
  'available',
  'assigned',
  'checked_out',
  'in_transit',
  'in_repair',
  'inspection_pending',
  'lost',
  'stolen',
  'damaged',
  'missing',
  'awaiting_disposal',
  'retired',
  'sold',
  'donated',
  'recycled',
  'disposed',
  // original
  'repair',
] as const
export type AssetStatus = (typeof ASSET_STATUSES)[number]
export const ASSET_STATUS_LABELS: Record<AssetStatus, string> = {
  available: 'Available',
  assigned: 'Assigned',
  checked_out: 'Checked out',
  in_transit: 'In transit',
  in_repair: 'In repair',
  inspection_pending: 'Inspection pending',
  lost: 'Lost',
  stolen: 'Stolen',
  damaged: 'Damaged',
  missing: 'Missing — review',
  awaiting_disposal: 'Awaiting disposal',
  retired: 'Retired',
  sold: 'Sold',
  donated: 'Donated',
  recycled: 'Recycled',
  disposed: 'Disposed',
  repair: 'In repair',
}
export const normalizeAssetStatus = (s: string | null | undefined): AssetStatus => (s === 'repair' ? 'in_repair' : s && (ASSET_STATUSES as readonly string[]).includes(s) ? (s as AssetStatus) : 'available')
/** Out of the active fleet for good. */
export const END_OF_LIFE_STATUSES: readonly AssetStatus[] = ['retired', 'sold', 'donated', 'recycled', 'disposed']
/** Not in hand and not accounted for in the ordinary way. */
export const LOSS_STATUSES: readonly AssetStatus[] = ['lost', 'stolen', 'missing']
export const isActiveAsset = (s: string | null | undefined) => !END_OF_LIFE_STATUSES.includes(normalizeAssetStatus(s))

// ─────────────────────────────────────────────────────────────────────────────
// The asset
// ─────────────────────────────────────────────────────────────────────────────

export const CUSTODIAN_TYPES = ['student', 'staff', 'room', 'department', 'school', 'activity', 'vehicle', 'storage'] as const
export type CustodianType = (typeof CUSTODIAN_TYPES)[number]
export const CUSTODIAN_TYPE_LABELS: Record<CustodianType, string> = { student: 'Student', staff: 'Staff member', room: 'Room', department: 'Department', school: 'School', activity: 'Team / activity', vehicle: 'Vehicle', storage: 'Storage location' }
/** The original three are still what other modules write. */
export const ASSIGNEE_TYPES = CUSTODIAN_TYPES
export type AssigneeType = CustodianType

export interface AssetAccessory {
  key: string
  label: string
  /** A required accessory missing at return opens a loss record. */
  required: boolean
}

export interface AssetDocumentRef {
  id: string
  kind: 'purchase' | 'warranty' | 'manual' | 'photo' | 'repair_report' | 'disposal_certificate' | 'donation_record' | 'transfer' | 'sanitization' | 'other'
  title: string
  reference: string | null
  blobPath: string | null
  at: string
  byName: string
}

export interface Asset extends BaseDocument {
  docType: 'asset'
  /** The human identifier: AST-10025. Never the database id. */
  assetNumber?: string | null
  /** The district property number on the sticker; may equal the asset number. Unique. */
  assetTag: string
  barcode?: string | null
  /** Opaque token in the QR — nothing about the asset or its holder can be read from it. */
  qrToken?: string | null
  serialNumber?: string | null
  type: AssetType | string
  /** District category path, e.g. "Technology / Student Devices". */
  category?: string | null
  manufacturer?: string | null
  model?: string
  description?: string | null
  condition: AssetCondition
  status: AssetStatus
  // ── owner, location, custodian — three different things ──
  owningSchoolId?: string | null
  owningDepartment?: string | null
  currentSchoolId?: string | null
  currentLocationId?: string | null
  /** Free-text location — the original field; kept current as a label. */
  location?: string
  custodian?: { type: CustodianType; id: string; name: string; assignmentId: string; kind: AssignmentKind } | null
  // ── acquisition (Purchasing is the source; these are references) ──
  purchaseDate?: string | null
  purchasePriceCents?: number | null
  replacementCostCents?: number | null
  vendor?: string | null
  vendorId?: string | null
  purchaseRequestId?: string | null
  purchaseOrderId?: string | null
  purchaseOrderLineId?: string | null
  invoiceReference?: string | null
  receivedOn?: string | null
  fundingSource?: string | null
  grant?: { grantId: string; reportingRequirements: string | null; disposalRestriction: string | null } | null
  capital?: { isCapital: boolean; category: string | null; accountingReference: string | null } | null
  /** Depreciation-ready fields for a future fixed-asset system. Nothing is calculated here. */
  depreciation?: { usefulLifeYears: number | null; salvageValueCents: number | null; method: string | null; externalFixedAssetId: string | null } | null
  // ── warranty ──
  warrantyExpiresOn?: string | null
  warranty?: { provider: string | null; start: string | null; end: string | null; type: string | null; coverage: string | null; documentId: string | null } | null
  // ── lifecycle ──
  expectedEndOfLife?: string | null
  dataBearing?: boolean
  sanitizationStatus?: 'not_required' | 'pending' | 'completed' | 'verified' | null
  replacesAssetId?: string | null
  replacedByAssetId?: string | null
  retiredOn?: string | null
  retiredReason?: AssetRetirementReason | null
  // ── kit, sizing, photo, documents ──
  accessories?: AssetAccessory[]
  sizing?: { size: string | null; number: string | null; season: string | null } | null
  photoBlobPath?: string | null
  documents?: AssetDocumentRef[]
  notes?: string
  // ── labels ──
  labelPrintCount?: number
  labelPrintedAt?: string | null
  labelPrintedBy?: string | null
  /** Draft records from receiving wait for their serial and tag before they are in service. */
  draft?: boolean
  // ── stage 2: where it was last counted, and what is open on it ──
  lastInventoriedAt?: string | null
  lastInventorySessionId?: string | null
  openTransferId?: string | null
  openIncidentId?: string | null
  /** The work order a repair runs on — Facilities owns it. */
  repairWorkOrderId?: string | null
  // ── integration readiness (no vendor connected) ──
  externalAssetId?: string | null
  externalSystemType?: string | null
  syncStatus?: string | null
  lastSyncedAt?: string | null
  version?: number
}

export const ASSET_RETIREMENT_REASONS = ['end_of_life', 'beyond_repair', 'obsolete', 'replaced', 'lost_write_off', 'other', 'surplus', 'sold', 'donated', 'recycled', 'destroyed'] as const
export type AssetRetirementReason = (typeof ASSET_RETIREMENT_REASONS)[number]
export const RETIREMENT_REASON_LABELS: Record<AssetRetirementReason, string> = { end_of_life: 'End of life', beyond_repair: 'Beyond repair', obsolete: 'Obsolete', replaced: 'Replaced', lost_write_off: 'Lost write-off', other: 'Other', surplus: 'Surplus', sold: 'Sold', donated: 'Donated', recycled: 'Recycled', destroyed: 'Destroyed' }

/**
 * One claim per identifier ever used. The document id IS the identifier, so a
 * duplicate fails at create. Retiring or re-tagging an asset keeps its claim:
 * the old sticker still points at the asset it was on.
 */
export interface AssetTagClaim extends BaseDocument {
  docType: 'assetTagClaim'
  assetId: string
  identifierType?: 'tag' | 'asset_number' | 'barcode' | 'qr' | 'serial' | null
  /** No longer the asset's current identifier — an alias that may still resolve. */
  superseded?: boolean
}
export const assetTagClaimId = (tag: string) => `atag-${tag.replace(/\s+/g, '')}`

/** Every identifier the asset has carried, with when and why it changed. */
export interface AssetIdentifier extends BaseDocument {
  docType: 'assetIdentifier'
  assetId: string
  identifierType: 'tag' | 'asset_number' | 'barcode' | 'qr' | 'serial'
  value: string
  effectiveStart: string
  effectiveEnd: string | null
  reason: string
  byName: string
}

/** The short prefix each asset type carries in a generated tag. */
export const ASSET_TAG_PREFIXES: Record<string, string> = {
  chromebook: 'CHRM',
  laptop: 'LAP',
  desktop: 'DSK',
  tablet: 'TAB',
  monitor: 'MON',
  printer: 'PRN',
  projector: 'PRJ',
  camera: 'CAM',
  radio: 'RAD',
  phone: 'PHN',
  hotspot: 'HOT',
  calculator: 'CALC',
  textbook: 'TXT',
  furniture: 'FRN',
  instrument: 'INST',
  athletic_equipment: 'ATH',
  uniform: 'UNI',
  kitchen_equipment: 'KIT',
  maintenance_equipment: 'MNT',
  vehicle_equipment: 'VEH',
  av_equipment: 'AV',
  aed: 'AED',
  equipment: 'EQP',
  other: 'OTH',
}

export const assetNumberFor = (n: number) => `AST-${String(n).padStart(5, '0')}`

// ─────────────────────────────────────────────────────────────────────────────
// Assignments, checkouts, loaners
// ─────────────────────────────────────────────────────────────────────────────

/** Long-term custody, a temporary checkout, or a loaner while another asset is away. */
export const ASSIGNMENT_KINDS = ['assignment', 'checkout', 'loaner'] as const
export type AssignmentKind = (typeof ASSIGNMENT_KINDS)[number]

/**
 * One custody record. `returnedOn: null` is the live one; history rows keep
 * the rest. Never edited into someone else's custody — reassigning ends this
 * row and starts a new one.
 */
export interface AssetAssignment extends BaseDocument {
  docType: 'assetAssignment'
  assetId: string
  kind?: AssignmentKind
  assigneeType: AssigneeType
  assigneeId: string
  /** Denormalized for the printed agreement and the history list. */
  assigneeName: string
  issuedOn: string
  /** Exact time for checkouts and scans. */
  startAt?: string | null
  expectedReturnOn?: string | null
  dueAt?: string | null
  returnedOn?: string | null
  endedAt?: string | null
  conditionOut: AssetCondition
  conditionIn?: AssetCondition | null
  notes?: string
  purpose?: string | null
  locationId?: string | null
  locationName?: string | null
  accessoriesOut?: string[]
  accessoriesIn?: string[]
  agreementFormInstanceId?: string | null
  /** The asset this loaner stands in for. */
  loanerForAssetId?: string | null
  /** Where the custody came from: an activity, bulk distribution, a transfer, Library… */
  source?: { module: string; entityId: string | null; label: string | null } | null
  /** Issued for an activity (a helmet for football, a uniform for band). */
  activityId?: string | null
  /** The asset as it was when issued — a later model or serial fix never rewrites this. */
  snapshot?: { assetNumber: string | null; assetTag: string; type: string; model: string | null; serialNumber: string | null; locationName: string | null } | null
  returnedByName?: string | null
  receivedByName?: string | null
  outcome?: 'returned' | 'returned_damaged' | 'lost' | 'reassigned' | 'transferred' | 'retired' | null
  issuedByName?: string | null
}

// ─────────────────────────────────────────────────────────────────────────────
// Timeline
// ─────────────────────────────────────────────────────────────────────────────

export const ASSET_EVENT_KINDS = ['acquired', 'tagged', 'retagged', 'assigned', 'checked_out', 'returned', 'transferred', 'received', 'inventoried', 'damaged', 'sent_to_repair', 'repair_complete', 'lost', 'found', 'status_changed', 'label_printed', 'retired', 'disposed', 'sanitized', 'updated', 'fee_requested'] as const
export type AssetEventKind = (typeof ASSET_EVENT_KINDS)[number]

/** One event on an asset's life. Append-only: the timeline is the record. */
export interface AssetEvent extends BaseDocument {
  docType: 'assetEvent'
  assetId: string
  kind: AssetEventKind
  at: string
  byName: string
  summary: string
  detail?: Record<string, unknown> | null
  ref?: { module: string; id: string; label: string | null } | null
}

// ─────────────────────────────────────────────────────────────────────────────
// Settings
// ─────────────────────────────────────────────────────────────────────────────

export interface AssetTypeConfig {
  key: string
  label: string
  category: string
  active: boolean
  /** Expected lifecycle in years (age vs expected is shown; nothing auto-retires). */
  lifecycleYears: number | null
  dataBearing: boolean
  accessories: AssetAccessory[]
  sized: boolean
  /** Return policy: back to Available, or to an inspection hold first. */
  returnTo: 'available' | 'inspection'
  defaultLabelTemplate: string | null
  /** Operations and Emergency read these (AED, radios, generators). */
  critical: boolean
}

export interface AssetSettingsDoc {
  propertyText: string | null
  tagPrefix: string | null
  /** Asset tag equals the asset number (AST-10025) unless the district uses separate property numbers. */
  tagStyle: 'asset_number' | 'typed_prefix' | 'manual'
  types: AssetTypeConfig[]
  conditions: { key: AssetCondition; label: string; active: boolean }[]
  checkout: { defaultDays: number; dueSoonHours: number }
  assignment: { requireAgreementForStudents: boolean; agreementFormTemplateId: string | null; allowRoomCustody: boolean }
  fees: { studentLossFeeDefinitionId: string | null; studentDamageFeeDefinitionId: string | null; staffLossRoute: 'hr' | 'finance' | 'none' }
  retirement: { approvalRequired: boolean; approverRoles: string[]; dispositions: string[] }
  aliasesResolve: boolean
  locked: { assetNumberPattern: boolean; disposalPolicy: boolean; requiredFields: boolean; tagStandards: boolean }
  schools: Record<string, { managerStaffIds: string[]; defaultLocationId: string | null; inventoryCadenceDays: number | null }>
  nextAssetNumber?: number
}

const acc = (key: string, label: string, required = true): AssetAccessory => ({ key, label, required })
const type = (key: AssetType, category: string, o: Partial<AssetTypeConfig> = {}): AssetTypeConfig => ({ key, label: ASSET_TYPE_LABELS[key], category, active: true, lifecycleYears: null, dataBearing: false, accessories: [], sized: false, returnTo: 'available', defaultLabelTemplate: '1x2', critical: false, ...o })

export const DEFAULT_ASSET_SETTINGS: AssetSettingsDoc = {
  propertyText: null,
  tagPrefix: null,
  tagStyle: 'typed_prefix',
  types: [
    type('chromebook', 'Technology / Student Devices', { lifecycleYears: 5, dataBearing: true, accessories: [acc('charger', 'Charger'), acc('case', 'Case', false)], returnTo: 'inspection' }),
    type('laptop', 'Technology / Staff Devices', { lifecycleYears: 5, dataBearing: true, accessories: [acc('charger', 'Charger')], returnTo: 'inspection' }),
    type('desktop', 'Technology / Computers', { lifecycleYears: 6, dataBearing: true }),
    type('tablet', 'Technology / Student Devices', { lifecycleYears: 4, dataBearing: true, accessories: [acc('charger', 'Charger'), acc('stylus', 'Stylus', false)] }),
    type('monitor', 'Technology / Peripherals', { lifecycleYears: 7 }),
    type('printer', 'Technology / Peripherals', { lifecycleYears: 6 }),
    type('projector', 'Technology / AV', { lifecycleYears: 7, defaultLabelTemplate: '2.25x1.25' }),
    type('camera', 'Technology / AV', { lifecycleYears: 6, accessories: [acc('battery', 'Battery'), acc('charger', 'Charger')] }),
    type('radio', 'Operations / Communications', { lifecycleYears: 7, accessories: [acc('charger', 'Charger')], critical: true }),
    type('phone', 'Technology / Staff Devices', { lifecycleYears: 3, dataBearing: true, accessories: [acc('charger', 'Charger')] }),
    type('hotspot', 'Technology / Student Devices', { lifecycleYears: 3, accessories: [acc('charger', 'Charger')] }),
    type('calculator', 'Instruction / Classroom', { lifecycleYears: 8 }),
    type('textbook', 'Instruction / Textbooks', { lifecycleYears: 6 }),
    type('furniture', 'Facilities / Furniture', { lifecycleYears: 15, defaultLabelTemplate: '2x1' }),
    type('instrument', 'Fine Arts / Instruments', { lifecycleYears: 15, accessories: [acc('case', 'Case')] }),
    type('athletic_equipment', 'Athletics / Equipment', { lifecycleYears: 10, sized: true }),
    type('uniform', 'Athletics / Uniforms', { lifecycleYears: 5, sized: true }),
    type('kitchen_equipment', 'Food Service / Equipment', { lifecycleYears: 12, critical: true }),
    type('maintenance_equipment', 'Facilities / Equipment', { lifecycleYears: 10 }),
    type('vehicle_equipment', 'Transportation / Equipment', { lifecycleYears: 8 }),
    type('av_equipment', 'Technology / AV', { lifecycleYears: 7, defaultLabelTemplate: '2.25x1.25' }),
    type('aed', 'Health & Safety / Emergency Equipment', { lifecycleYears: 10, critical: true }),
    type('equipment', 'General / Equipment', { lifecycleYears: 10 }),
    type('other', 'General / Other'),
  ],
  conditions: (['new', 'excellent', 'good', 'fair', 'poor', 'damaged', 'nonfunctional'] as const).map((key) => ({ key, label: ASSET_CONDITION_LABELS[key], active: true })),
  checkout: { defaultDays: 7, dueSoonHours: 24 },
  assignment: { requireAgreementForStudents: false, agreementFormTemplateId: null, allowRoomCustody: true },
  fees: { studentLossFeeDefinitionId: null, studentDamageFeeDefinitionId: null, staffLossRoute: 'hr' },
  retirement: { approvalRequired: true, approverRoles: ['it_admin', 'school_admin'], dispositions: ['recycle', 'dispose', 'sell', 'auction', 'donate', 'parts', 'return_to_vendor'] },
  aliasesResolve: true,
  locked: { assetNumberPattern: true, disposalPolicy: true, requiredFields: false, tagStandards: true },
  schools: {},
}

/** The original settings document, still read for property text and tag prefix. */
export interface AssetSettings extends BaseDocument, Partial<AssetSettingsDoc> {
  docType: 'assetSettings'
  propertyText?: string | null
  tagPrefix?: string | null
}

/**
 * An inventory walk: open it against a scope, scan tags into it, close it to
 * a seen / missing / unexpected summary. Stage 2 adds sessions with
 * exceptions and reconciliation; this shape stays readable.
 */
export interface InventorySession extends BaseDocument {
  docType: 'inventorySession'
  scopeLocation?: string | null
  scopeType?: AssetType | null
  startedBy: string
  startedByName: string
  startedAt: string
  status: 'open' | 'closed'
  expectedTags: string[]
  scannedTags: string[]
  unexpectedTags: string[]
  closedAt?: string | null
  summary?: { seen: number; missing: string[]; unexpected: string[] } | null
}
